import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface StickyBottomBarProps {
  children: ReactNode;
  className?: string;
  /** Only show on mobile (< md). Default: true */
  mobileOnly?: boolean;
  /** Bottom offset in px above safe-area. Default: 72 (for pages with bottom nav). Set to 0 for public pages without bottom nav. */
  bottomOffsetPx?: number;
}

/**
 * StickyBottomBar – fixed action bar at bottom of screen.
 * Sits above the mobile bottom nav (72px) + safe-area-inset by default.
 * Set bottomOffsetPx={0} on public pages without bottom nav.
 */
export function StickyBottomBar({ children, className, mobileOnly = true, bottomOffsetPx = 72 }: StickyBottomBarProps) {
  const isMobile = useIsMobile();

  if (mobileOnly && !isMobile) return null;

  return (
    <div
      className={cn(
        'fixed left-0 right-0 z-40',
        'border-t bg-background/95 backdrop-blur-sm',
        'px-4 py-3',
        className,
      )}
      style={{
        bottom: bottomOffsetPx > 0
          ? `calc(${bottomOffsetPx}px + env(safe-area-inset-bottom, 0px))`
          : 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="flex items-center justify-end gap-3 max-w-xl mx-auto">
        {children}
      </div>
    </div>
  );
}
